import React, { useState, useRef, useEffect, useCallback } from 'react';

const ReviewsCarousel = ({ reviews, intervalMs = 4000 }) => {
  const count = reviews.length;
  // Start in the middle copy so we always have buffer cards on both sides
  const [index, setIndex] = useState(count);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [noTransition, setNoTransition] = useState(false);
  const [, forceRecalc] = useState(0);

  const viewportRef = useRef(null);
  const cardRef = useRef(null);
  const dragStartX = useRef(0);
  const pointerId = useRef(null);

  // Render 3 copies of the review list so the track always has neighbours to slide to/from
  const trackItems = [...reviews, ...reviews, ...reviews].map((review, i) => ({
    ...review,
    key: `${i}-${review.name}`
  }));

  const getCardWidth = useCallback(() => {
    return cardRef.current ? cardRef.current.offsetWidth : 0;
  }, []);

  // Recompute the card width on viewport resize so the slide offset stays accurate
  useEffect(() => {
    const handleResize = () => forceRecalc((n) => n + 1);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const goTo = useCallback((newIndex) => {
    setIndex(newIndex);
  }, []);

  // Autoplay, paused while dragging
  useEffect(() => {
    if (isDragging || count <= 1) return undefined;
    const timer = setInterval(() => {
      setIndex((prev) => prev + 1);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [isDragging, intervalMs, count]);

  // Keep the index within the middle copy so looping never runs out of cards
  useEffect(() => {
    if (index >= count * 2) {
      setNoTransition(true);
      setIndex(index - count);
    } else if (index < count) {
      setNoTransition(true);
      setIndex(index + count);
    }
  }, [index, count]);

  useEffect(() => {
    if (noTransition) {
      const id = requestAnimationFrame(() => setNoTransition(false));
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [noTransition]);

  const handlePointerDown = (e) => {
    setIsDragging(true);
    dragStartX.current = e.clientX;
    pointerId.current = e.pointerId;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    setDragOffset(e.clientX - dragStartX.current);
  };

  const endDrag = () => {
    if (!isDragging) return;
    const cardWidth = getCardWidth();
    if (cardWidth > 0) {
      const cardsMoved = Math.round(-dragOffset / cardWidth);
      if (cardsMoved !== 0) {
        setIndex((prev) => prev + cardsMoved);
      }
    }
    setIsDragging(false);
    setDragOffset(0);
  };

  const handlePointerUp = () => endDrag();
  const handlePointerLeave = () => endDrag();

  const cardWidth = getCardWidth();
  const baseTranslate = -index * cardWidth;
  const translateX = baseTranslate + (isDragging ? dragOffset : 0);

  return (
    <div className="reviews-carousel" ref={viewportRef}>
      <div
        className="reviews-carousel-track"
        style={{
          transform: `translateX(${translateX}px)`,
          transition: isDragging || noTransition ? 'none' : 'transform 0.4s ease',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
      >
        {trackItems.map((review, i) => (
          <div className="reviews-carousel-card" key={review.key} ref={i === 0 ? cardRef : null}>
            <div className="review-card review-card-image">
              <img src={review.image} alt={`Avis de ${review.name}`} draggable="false" />
              <div className="review-author">
                <strong>{review.name}</strong>
                <i className="fas fa-check-circle" style={{ color: '#10b981', marginLeft: '5px' }} title="Client vérifié"></i>
              </div>
            </div>
          </div>
        ))}
      </div>

      {count > 1 && (
        <div className="reviews-carousel-dots">
          {reviews.map((_, i) => (
            <button
              type="button"
              key={i}
              className={`reviews-carousel-dot ${((index % count) + count) % count === i ? 'active' : ''}`}
              onClick={() => goTo(count + i)}
              aria-label={`Aller à l'avis ${i + 1}`}
            ></button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsCarousel;
